import React, { useMemo, useState } from 'react';
import { Layout, Row } from 'antd';
import {
  DragDropContext, Droppable, Draggable, DropResult,
} from 'react-beautiful-dnd';
import { Button, ButtonType } from 'components/Controls/Button';
import { Checkbox } from 'components/Controls/Checkbox';
import { SearchInput } from 'components/Controls/Input/SearchInput';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import {
  filterBySearch,
  filterConfigurable,
  sortConfig,
  ConfigItem,
} from './utils';
import cn from './styles.module.scss';

const { Content } = Layout;

export type ColumnConfigProps = {
  onClose: () => void;
  onChange: (settings: string[], order: string[]) => void;
  config: ConfigItem[],
  defaultSettings?: string[];
  defaultOrder?: string[];
  searchable?: boolean;
  sortable?: boolean;
};

type ItemProps = {
  item: ConfigItem;
  settings: string[];
  handleChange: (key: string, columnKey: string, event: CheckboxChangeEvent) => void,
  handleChangeGroup: (key: string, event: CheckboxChangeEvent) => void,
};

const Item = (props: ItemProps) => {
  const {
    item,
    settings,
    handleChange,
    handleChangeGroup,
  } = props;

  const isIndeterminate = (childItems: ConfigItem[]): boolean => (
    !!childItems.filter((column) => settings.includes(column.key)
      && childItems.filter((innerColumn) => settings.includes(innerColumn.key)).length !== childItems.length).length
  );

  return (
    (
      <div className={cn.editColumnGroup} data-testid="configItem" data-test-title={item.title}>
        {item.children
          ? (
            <>
              <Checkbox
                checked={
                  item.children.filter((column: any) => settings.includes(column.key)).length === item.children.length
                }
                indeterminate={isIndeterminate(item.children)}
                onChange={(event) => handleChangeGroup(item.key, event)}
                className={cn.checkbox}
                data-testid={`${item.title}_checkbox`}
              >
                <span className={cn.checkboxTitle}>
                  {item.title}
                  :
                </span>
              </Checkbox>
              <div className={cn.groupItemsContainer}>
                <div className={cn.divider} />
                <div className={cn.groupItems}>
                  {item.children.map((column: any) => (
                    <Checkbox
                      key={column.key}
                      checked={settings.includes(column.key)}
                      onChange={(event) => handleChange(item.key, column.key, event)}
                      className={cn.checkbox}
                      data-testid={`${column.editTitle || column.title}_checkbox`}
                    >
                      <span className={cn.checkboxTitle}>{column.editTitle || column.title}</span>
                    </Checkbox>
                  ))}
                </div>
              </div>
            </>
          )
          : (
            <Checkbox
              key={item.key}
              checked={settings.includes(item.key)}
              onChange={(event) => handleChange(item.key, item.key, event)}
              className={cn.checkbox}
              data-testid={`${item.title}_checkbox`}
            >
              <span className={cn.checkboxTitle}>{item.title}</span>
            </Checkbox>
          )}
      </div>
    )
  );
};

export const EditConfig = React.memo(({
  onClose,
  onChange,
  config,
  defaultSettings = [],
  defaultOrder = [],
  searchable,
  sortable,
}: ColumnConfigProps) => {
  const [search, setSearch] = useState('');
  const [settings, setSettings] = useState(defaultSettings);
  const [order, setOrder] = useState(defaultOrder);

  const configuredConfig = useMemo(() => {
    const filteredConfig = filterBySearch(filterConfigurable(config), search);
    return sortable ? sortConfig(filteredConfig, order) : filteredConfig;
  }, [config, search, order]);

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) {
      return;
    }

    const oldIndex = source.index;
    const newIndex = destination.index;
    const newOrder = [...order];

    newOrder.splice(newIndex, 0, newOrder.splice(oldIndex, 1)[0]);
    setOrder(newOrder);
  };

  const handleOrderReset = () => {
    setOrder(defaultOrder);
  };

  const handleChange = (key: string, columnKey: string, event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    if (checked) {
      setSettings([...settings, columnKey]);
    } else {
      setSettings(settings.filter((setting) => setting !== columnKey));
    }
  };

  const handleChangeGroup = (key: string, event: CheckboxChangeEvent) => {
    const { checked } = event.target;
    const columnKeys = configuredConfig.find((item) => item.key === key)?.children?.map((column) => column.key)
      || [];
    if (checked) {
      const newSettings = [...settings];
      columnKeys.forEach((columnKey) => {
        if (!settings.includes(columnKey)) {
          newSettings.push(columnKey);
        }
      });
      setSettings(newSettings);
    } else {
      setSettings(settings.filter((setting) => !columnKeys.includes(setting)));
    }
  };

  const handleSubmit = () => {
    onChange(settings, order);
    onClose();
  };

  return (
    <div className={cn.editColumnContainer}>
      <Row>
        <Content>
          {sortable && (
            <div className={cn.resetContainer}>
              <Button
                onClick={handleOrderReset}
                type={ButtonType.Tertiary}
                size="medium"
              >
                Reset Fields Order
              </Button>
            </div>
          )}
          {searchable && (
            <SearchInput onChange={setSearch} debounceTime={100} data-testid="searchInput" />
          )}
        </Content>
      </Row>
      <Row>
        <Content>
          {
            sortable
              ? (
                <DragDropContext onDragEnd={onDragEnd}>
                  <div className={cn.editColumnGroup}>
                    <Droppable droppableId="config">
                      {(droppableProvided) => (
                        <div
                          {...droppableProvided.droppableProps}
                          ref={droppableProvided.innerRef}
                        >
                          {configuredConfig.map((item, index) => (
                            <Draggable key={item.key} draggableId={item.key} index={index}>
                              {(draggableProvided) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                >
                                  <Item
                                    item={item}
                                    settings={settings}
                                    handleChange={handleChange}
                                    handleChangeGroup={handleChangeGroup}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </DragDropContext>
              )
              : configuredConfig.map((item) => (
                <Item
                  key={item.key}
                  item={item}
                  settings={settings}
                  handleChange={handleChange}
                  handleChangeGroup={handleChangeGroup}
                />
              ))
          }
        </Content>
      </Row>
      <Row justify="end" className={cn.editColumnFooter}>
        <Button size="large" type={ButtonType.Secondary} onClick={onClose}>Cancel</Button>
        <Button size="large" type={ButtonType.Primary} onClick={handleSubmit}>Update</Button>
      </Row>
      <div className={cn.placeholder} />
    </div>
  );
});
